export interface IBaseIdentityProvider {
    domains: string[];
    expiresAt?: string;
    scimApiKey?: string;
    id: string;
    idpId?: string;
    idpName: string;
    issuerUrl: string | undefined;
    type: "SAML" | "OIDC"; // This is the discriminator
}

export interface ISAMLIdp extends IBaseIdentityProvider {
    acsUrl: string;
    audienceUrl: string;
    ssoUrl: string | null;
    expiresAt: string;
    type: "SAML";
}

export interface IOIDCIdp extends IBaseIdentityProvider {
    authorizationUrl?: string;
    clientId: string;
    clientSecret: string;
    jwksUrl?: string;
    provider: string;
    tokenUrl?: string;
    type: "OIDC";
    userInfoUrl?: string;
    wellKnownUrl?: string;
}

export type SAMLIdpUpdateFields = Pick<ISAMLIdp, "id" | "idpName" | "issuerUrl" | "ssoUrl">;

export type IIdentityProvider = IOIDCIdp | ISAMLIdp;
export type OIDCIdpWithoutId = Omit<IOIDCIdp, "id" | "domains">;
export type SAMLIdpWithoutId = Omit<ISAMLIdp, "id" | "domains">;
export type IdpWithoutId = Omit<IIdentityProvider, "id" | "domains">;

export class IdentityProvider implements IBaseIdentityProvider {
    constructor({ issuerUrl, ...rest }: IIdentityProvider) {
        this.issuerUrl = issuerUrl;
        this.type = rest.type;
        this.id = rest?.id;
        this.idpName = rest?.idpName || "Connection";
        this.idpId = rest?.idpId;
        this.domains = rest?.domains || [];
        this.scimApiKey = rest?.scimApiKey;
        if (this.type === "SAML") {
            // saml
            const { acsUrl, audienceUrl, ssoUrl, expiresAt } = rest as ISAMLIdp;
            this.audienceUrl = audienceUrl;
            this.acsUrl = acsUrl;
            this.ssoUrl = ssoUrl;
            this.expiresAt = expiresAt;
        } else {
            // oidc
            const { authorizationUrl, clientId, clientSecret, jwksUrl, provider, tokenUrl, userInfoUrl, wellKnownUrl } =
                rest as IOIDCIdp;
            this.authorizationUrl = authorizationUrl;
            this.clientId = clientId;
            this.clientSecret = clientSecret;
            this.jwksUrl = jwksUrl;
            this.provider = provider;
            this.tokenUrl = tokenUrl;
            this.userInfoUrl = userInfoUrl;
            this.wellKnownUrl = wellKnownUrl;
        }
    }

    audienceUrl?: string;
    authorizationUrl?: string;
    acsUrl?: string;
    clientId?: string;
    clientSecret?: string;
    domains: string[]; // todo: non-optional
    scimApiKey?: string;
    id: string;
    idpId?: string;
    idpName: string;
    issuerUrl: string | undefined;
    jwksUrl?: string;
    provider?: string;
    ssoUrl?: string | null;
    expiresAt?: string;
    tokenUrl?: string;
    type: "OIDC" | "SAML";
    userInfoUrl?: string;
    wellKnownUrl?: string;
}

/**
 * SCIM is considered active for an IdP when a SCIM API key is present. When enabled, user
 * provisioning is owned by the IdP via SCIM, so domain-based dynamic provisioning must be
 * disabled in the UI. This is the single source of truth for the "is SCIM active?" business
 * rule — consume it instead of inspecting `scimApiKey` directly in components.
 *
 * Implemented as a helper rather than a class getter so it also works on the plain-object
 * IdP shapes used throughout the codebase without forcing a required member onto the class.
 */
export const isScimEnabled = (idp: Pick<IBaseIdentityProvider, "scimApiKey">): boolean => !!idp.scimApiKey?.length;
